import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { createTransfer, getCashDay, listCashHolders } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import { istToday, num, refusalText } from "../money";
import type { Holder } from "../types";
import Keypad from "../ui/Keypad";
import "../cashbook.scss";
import "./transfer.scss";

/**
 * नकद दें…: hand cash to another supervisor (V1_SPEC 11.5 B1, 7.11). It leaves the giver's cash in hand at
 * once and reaches the receiver only when he taps "मिल गए" on his own phone. More than the cash in hand
 * is allowed with a note (D22). Not queued offline: no network means nothing changed.
 */
const GiveScreen = () => {
  const { t } = useKT();
  const history = useHistory();
  const status = useCashbookStatus();
  const [holders, setHolders] = useState<Holder[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inHand, setInHand] = useState<string | null>(null);
  const [to, setTo] = useState<Holder | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One id for this handover: a second tap or a retry after a lost answer gives it once.
  const [requestId] = useState(newRequestId);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await listCashHolders();
      if (res?.success && res.data) {
        setHolders(res.data.holders || []);
        if (res.data.holders?.length === 1) setTo(res.data.holders[0]);
      } else setLoadError(refusalText(res, t));
    } catch {
      setLoadError(t("Couldn't load. Check the network."));
    }
    getCashDay(istToday())
      .then((res) => setInHand(res?.success ? res.data?.cash?.inHand ?? null : null))
      .catch(() => setInHand(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const give = async () => {
    if (!to || num(amount) <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createTransfer({ toUserId: to.userId, amount, note: note.trim() || null, clientRequestId: requestId });
      if (!res?.success || !res.data) {
        setError(refusalText(res, t));
        return;
      }
      notifications({
        title: t("Give {{amount}} to {{name}}", { amount: rupees(amount), name: to.name }),
        message: t("Cash in hand {{amount}}", { amount: rupees(res.data.cashInHand) }),
        type: "success",
      });
      status.refresh();
      history.push(kp.cashBook());
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };

  const header = <PageHeader title={t("Hand cash to…")} back={kp.cashBook()} backLabel={t("Back")} />;

  if (loadError && !holders)
    return (
      <div className="cb">
        {header}
        <div className="cb-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!holders)
    return (
      <div className="cb" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton" />
        <div className="cb-skeleton is-big" />
      </div>
    );

  return (
    <div className="cb">
      {header}
      <h3 className="cb-section">{t("To whom?")}</h3>
      {holders.length ? (
        <div className="cb-people" role="group" aria-label={t("To whom?")}>
          {holders.map((h) => (
            <button key={h.userId} type="button" className={`cb-chip${to?.userId === h.userId ? " is-on" : ""}`} aria-pressed={to?.userId === h.userId} onClick={() => setTo(h)}>
              {h.name}
            </button>
          ))}
        </div>
      ) : (
        <p className="cb-muted">{t("No other supervisor yet.")}</p>
      )}
      <Keypad value={amount} onChange={setAmount} label={t("Amount")} id="cb-give" />
      {inHand !== null && num(amount) > num(inHand) ? <p className="cb-muted">{t("You seem to have only {{amount}}", { amount: rupees(inHand) })}</p> : null}
      <label className="cb-field">
        <span className="cb-field__label">{t("For what work? (optional)")}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
      </label>
      <button type="button" className="cb-btn is-primary" disabled={!to || num(amount) <= 0 || busy} onClick={give}>
        {busy ? t("Saving…") : to ? t("Give {{amount}} to {{name}}", { amount: rupees(amount || 0), name: to.name }) : t("To whom?")}
      </button>
      {error ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/give — supervisors only (owners and managers do not hold cash, D43). */
const GiveCash = () => (
  <CashbookGuard supervisorOnly>
    <GiveScreen />
  </CashbookGuard>
);

export default GiveCash;
